import React, { useState, useEffect } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { PageContentLayout, PageHeaderLayout } from '../../../shared/layout';
import { Card, Row, Space, Table, Col, Modal, Spin, Tag, Typography, Grid, Divider, Pagination } from 'antd';
import { FormOutlined } from '@ant-design/icons';
import Button from '../../../shared/globalStyling/Button';
import {
  ButtonText,
  PrimaryTableRowText,
  StandardTableRowText,
  TableColumnTitle,
} from '../../../shared/globalStyling/styledText';
import { StarOutlined } from '@ant-design/icons';
import FilterBar from './components/FilterBar';
import { formatCurrency2SigFig, formatWholeNumberNaN } from '../../../../core/utils/campaigns';
import LaunchAdvertiser from './components/LaunchAdvertiser';
import moment from 'moment';
import StatusTag from '../../Campaigns/shared/StatusTag';
import { useTagContext } from '../../../../core/components/tags/useTagContext';

const { useBreakpoint } = Grid;
const AdvertiserMainView = ({
  data,
  currentOrgId,
  onCreateAdvertiser,
  filteredLocations,
  filteredAudiences,
  isSelfService,
  orgLevel,
}) => {
  const history = useHistory();
  const screens = useBreakpoint();
  // Only use cards for mobile (xs, sm) and tablet (md), desktop (lg, xl, xxl) uses table
  // screens.lg will be true for lg, xl, xxl screens
  const isMobile = !screens.md; // xs and sm only
  const isTablet = screens.md && !screens.lg; // md only
  const useCardLayout = !screens.lg; // Use cards for xs, sm, md; table for lg, xl, xxl
  const [openLaunch, setOpenLaunch] = useState(false);
  const getDefaultSortedData = (inputData) => {
    if (!inputData || inputData.length === 0) return inputData;
    return [...inputData].sort((a, b) => moment(b.startDate).unix() - moment(a.startDate).unix());
  };

  const [tableData, setTableData] = useState(() => getDefaultSortedData(data));
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [sortedInfo, setSortedInfo] = useState({
    columnKey: 'startDate',
    order: 'descend',
  });

  useEffect(() => {
    const savedPageSize = sessionStorage.getItem('advertiserMainPageSize');
    if (savedPageSize) {
      setPageSize(Number(savedPageSize));
    }

    const savedSorting = sessionStorage.getItem('advertiserMainSorting');
    if (savedSorting) {
      try {
        const parsedSorting = JSON.parse(savedSorting);
        setSortedInfo(parsedSorting);
      } catch (error) {
        console.error('Failed to parse saved sorting:', error);
      }
    }
  }, []);

  useEffect(() => {
    const savedSorting = sessionStorage.getItem('advertiserMainSorting');
    if (!savedSorting && data && data.length > 0) {
      setTableData(getDefaultSortedData(data));
    } else if (savedSorting) {
      setTableData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSetData = (newData) => {
    const savedSorting = sessionStorage.getItem('advertiserMainSorting');
    if (!savedSorting && newData && newData.length > 0) {
      setTableData(getDefaultSortedData(newData));
    } else {
      setTableData(newData);
    }
  };

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const savedPageSize = Number(sessionStorage.getItem('advertiserMainPageSize')) || 10;
      const savedCurrentPage = Number(sessionStorage.getItem('advertiserMainCurrentPage')) || 1;

      const maxPage = Math.ceil(tableData.length / savedPageSize);
      const validPage = savedCurrentPage <= maxPage ? savedCurrentPage : 1;

      if (validPage !== current) {
        setCurrent(validPage);
      }
    }
  }, [tableData, current]);

  const {
    fetchTags,
    tags,
    loadingTags,
    getBasicTagActivity,
    basicTagActivity,
    tagActivityLoading,
    tagActivityError,
    clearBasicTagActivity,
  } = useTagContext();

  const onFetchTags = () => {
    fetchTags({
      variables: {
        where: {
          orgs_some: {
            OR: [
              {
                parentOrg_some: {
                  id: currentOrgId,
                },
              },
              {
                id: currentOrgId,
              },
            ],
          },
          isDisabled: false,
        },
      },
    });
  };

  useEffect(() => {
    onFetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderMobileCard = (record) => {
    const handleCardClick = (e) => {
      // Only navigate if clicking on the card itself, not interactive elements
      if (e.target === e.currentTarget || e.target.closest('.card-clickable-area')) {
        history.push(`/advertiser/details/${record.id}`);
      }
    };

    return (
      <Card
        style={{
          marginBottom: isMobile ? 12 : 16,
          borderRadius: 8,
        }}
        bodyStyle={{ padding: isMobile ? 16 : 20 }}
      >
        <Row gutter={[12, 8]}>
          {/* Header with Name and Status */}
          <Col span={24}>
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 4, cursor: 'pointer' }}
              className="card-clickable-area"
              onClick={handleCardClick}
            >
              <Col flex="auto">
                <Typography.Text strong style={{ fontSize: isMobile ? 14 : 16 }}>
                  {record.name || '-'}
                </Typography.Text>
              </Col>
              <Col>
                <StatusTag
                  startDate={
                    record && moment(record.startDate).seconds(0).milliseconds(0).toISOString()
                      ? moment(record.startDate).seconds(0).milliseconds(0).toISOString()
                      : undefined
                  }
                  endDate={
                    record && moment(record.endDate).seconds(0).milliseconds(0).toISOString()
                      ? moment(record.endDate).seconds(0).milliseconds(0).toISOString()
                      : undefined
                  }
                  attributionWindow={30}
                  statusCampaign={record.status && record.status}
                  impressions={record.totalImpressions}
                />
              </Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
          </Col>

          {/* Mobile: Impressions inline, then three columns below */}
          {isMobile && (
            <>
              <Col
                span={24}
                className="card-clickable-area"
                onClick={handleCardClick}
                style={{ cursor: 'pointer', paddingBottom: 8 }}
              >
                <Typography.Text strong style={{ fontSize: 12 }}>
                  Impressions:{' '}
                </Typography.Text>
                <Typography.Text strong style={{ fontSize: 12 }}>
                  {formatWholeNumberNaN(record.totalImpressions)}
                </Typography.Text>
              </Col>

              <Col span={24} onClick={(e) => e.stopPropagation()}>
                <Row gutter={[12, 8]}>
                  {/* Sales */}
                  <Col span={8}>
                    <Typography.Text strong style={{ fontSize: 11 }}>
                      Sales
                    </Typography.Text>
                    <div style={{ marginTop: 6 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                        Transactions
                      </Typography.Text>
                      <div>
                        <Typography.Text style={{ fontSize: 11 }}>
                          {formatWholeNumberNaN(record.transactions)}
                        </Typography.Text>
                      </div>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                        Revenue
                      </Typography.Text>
                      <div>
                        <Typography.Text style={{ fontSize: 11 }}>
                          {formatCurrency2SigFig(record.revenue)}
                        </Typography.Text>
                      </div>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                        Attr. Impr.
                      </Typography.Text>
                      <div>
                        <Typography.Text style={{ fontSize: 11 }}>
                          {formatWholeNumberNaN(record.impressions)}
                        </Typography.Text>
                      </div>
                    </div>
                  </Col>

                  {/* Signups */}
                  <Col span={8}>
                    <Typography.Text strong style={{ fontSize: 11 }}>
                      Signups
                    </Typography.Text>
                    <div style={{ marginTop: 6 }}>
                      <Typography.Text style={{ fontSize: 11 }}>
                        {formatWholeNumberNaN(record.signUps)}
                      </Typography.Text>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                        Attr. Impr.
                      </Typography.Text>
                      <div>
                        <Typography.Text style={{ fontSize: 11 }}>
                          {formatWholeNumberNaN(record.signUpsAttrImpressions)}
                        </Typography.Text>
                      </div>
                    </div>
                  </Col>

                  {/* Sessions */}
                  <Col span={8}>
                    <Typography.Text strong style={{ fontSize: 11 }}>
                      Sessions
                    </Typography.Text>
                    <div style={{ marginTop: 6 }}>
                      <Typography.Text style={{ fontSize: 11 }}>
                        {formatWholeNumberNaN(record.sessions)}
                      </Typography.Text>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                        Attr. Impr.
                      </Typography.Text>
                      <div>
                        <Typography.Text style={{ fontSize: 11 }}>
                          {formatWholeNumberNaN(record.sessionsAttrImpressions)}
                        </Typography.Text>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            </>
          )}

          {/* Tablet: All four metrics in one row */}
          {isTablet && (
            <Col
              span={24}
              className="card-clickable-area"
              onClick={handleCardClick}
              style={{ cursor: 'pointer' }}
            >
              <Row gutter={[12, 12]}>
                {/* Impressions */}
                <Col span={6}>
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    Impressions
                  </Typography.Text>
                  <div>
                    <Typography.Text strong style={{ fontSize: 13 }}>
                      {formatWholeNumberNaN(record.totalImpressions)}
                    </Typography.Text>
                  </div>
                </Col>

                {/* Sales */}
                <Col span={6}>
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    Sales
                  </Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      Transactions
                    </Typography.Text>
                    <div>
                      <Typography.Text style={{ fontSize: 11 }}>
                        {formatWholeNumberNaN(record.transactions)}
                      </Typography.Text>
                    </div>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      Revenue
                    </Typography.Text>
                    <div>
                      <Typography.Text style={{ fontSize: 11 }}>
                        {formatCurrency2SigFig(record.revenue)}
                      </Typography.Text>
                    </div>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      Attr. Impr.
                    </Typography.Text>
                    <div>
                      <Typography.Text style={{ fontSize: 11 }}>
                        {formatWholeNumberNaN(record.impressions)}
                      </Typography.Text>
                    </div>
                  </div>
                </Col>

                {/* Signups */}
                <Col span={6}>
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    Signups
                  </Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text style={{ fontSize: 11 }}>
                      {formatWholeNumberNaN(record.signUps)}
                    </Typography.Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      Attr. Impr.
                    </Typography.Text>
                    <div>
                      <Typography.Text style={{ fontSize: 11 }}>
                        {formatWholeNumberNaN(record.signUpsAttrImpressions)}
                      </Typography.Text>
                    </div>
                  </div>
                </Col>

                {/* Sessions */}
                <Col span={6}>
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    Sessions
                  </Typography.Text>
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text style={{ fontSize: 11 }}>
                      {formatWholeNumberNaN(record.sessions)}
                    </Typography.Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                      Attr. Impr.
                    </Typography.Text>
                    <div>
                      <Typography.Text style={{ fontSize: 11 }}>
                        {formatWholeNumberNaN(record.sessionsAttrImpressions)}
                      </Typography.Text>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  const columns = [
    {
      title: 'Funnel Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
      render: (text) => (text != null ? text : '-'),
    },
    {
      title: <TableColumnTitle text={'Status'} />,
      dataIndex: 'status',
      key: 'status',
      width: '6%',
      align: 'left',
      footerContent: '',
      render: (text, record) => {
        return (
          <Row type="flex" justify="start">
            <Col>
              <StatusTag
                startDate={
                  record && moment(record.startDate).seconds(0).milliseconds(0).toISOString()
                    ? moment(record.startDate).seconds(0).milliseconds(0).toISOString()
                    : undefined
                }
                endDate={
                  record && moment(record.endDate).seconds(0).milliseconds(0).toISOString()
                    ? moment(record.endDate).seconds(0).milliseconds(0).toISOString()
                    : undefined
                }
                attributionWindow={30}
                statusCampaign={record.status && record.status}
                impressions={record.totalImpressions}
              />
            </Col>
          </Row>
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      sorter: (a, b) => moment(a.startDate).unix() - moment(b.startDate).unix(),
      sortOrder: sortedInfo.columnKey === 'startDate' ? sortedInfo.order : null,
      render: (text) => (text ? moment(text).format('MM/DD/YYYY') : '-'),
    },
    {
      title: 'Impressions',
      dataIndex: 'totalImpressions',
      key: 'totalImpressions',
      sorter: (a, b) => a.totalImpressions - b.totalImpressions,
      sortOrder: sortedInfo.columnKey === 'totalImpressions' ? sortedInfo.order : null,
      render: (text) => formatWholeNumberNaN(text),
    },
    {
      title: 'Sales',
      dataIndex: 'transactions',
      key: 'transactions',
      sorter: (a, b) => a.transactions - b.transactions,
      sortOrder: sortedInfo.columnKey === 'transactions' ? sortedInfo.order : null,
      render: (text, record) => {
        return (
          <Row type="flex" justify="start" align="middle">
            <Col style={{ minWidth: '50px' }}>
              <>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Transactions:`} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatWholeNumberNaN(text)} />
                  </Col>
                </Row>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Revenue: `} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatCurrency2SigFig(record.revenue)} />
                  </Col>
                </Row>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Attr. Impressions: `} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatWholeNumberNaN(record.impressions)} />
                  </Col>
                </Row>
              </>
            </Col>
          </Row>
        );
      },
    },
    {
      title: 'Signups',
      dataIndex: 'signUps',
      key: 'signUps',
      sorter: (a, b) => a.signUps - b.signUps,
      sortOrder: sortedInfo.columnKey === 'signUps' ? sortedInfo.order : null,
      render: (text, record) => {
        return (
          <Row type="flex" justify="start" align="middle">
            <Col style={{ minWidth: '50px' }}>
              <>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Signups:`} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatWholeNumberNaN(text)} />
                  </Col>
                </Row>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Attr. Impressions: `} />
                  </Col>
                  <Col>
                    <StandardTableRowText
                      text={formatWholeNumberNaN(record.signUpsAttrImpressions)}
                    />
                  </Col>
                </Row>
              </>
            </Col>
          </Row>
        );
      },
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions',
      key: 'sessions',
      sorter: (a, b) => a.sessions - b.sessions,
      sortOrder: sortedInfo.columnKey === 'sessions' ? sortedInfo.order : null,
      render: (text, record) => {
        return (
          <Row type="flex" justify="start" align="middle">
            <Col style={{ minWidth: '50px' }}>
              <>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Sessions:`} />
                  </Col>
                  <Col>
                    <StandardTableRowText text={formatWholeNumberNaN(text)} />
                  </Col>
                </Row>
                <Row type="flex" gutter={[4, 4]}>
                  <Col>
                    <PrimaryTableRowText text={`Attr. Impressions: `} />
                  </Col>
                  <Col>
                    <StandardTableRowText
                      text={formatWholeNumberNaN(record.sessionsAttrImpressions)}
                    />
                  </Col>
                </Row>
              </>
            </Col>
          </Row>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Modal
        title={`Create Advertiser Funnel`}
        visible={openLaunch}
        footer={null}
        width={'75%'}
        onCancel={() => {
          setOpenLaunch(false);
        }}
      >
        <LaunchAdvertiser
          currentOrgId={currentOrgId}
          tags={tags}
          open={openLaunch}
          onCreate={(values) => {
            setOpenLaunch(false);
            onCreateAdvertiser(values);
          }}
          filteredLocations={filteredLocations}
          filteredAudiences={filteredAudiences}
          isSelfService={isSelfService}
          onFetchTags={onFetchTags}
          loadingTags={loadingTags}
          getBasicTagActivity={getBasicTagActivity}
          basicTagActivity={basicTagActivity}
          tagActivityLoading={tagActivityLoading}
          tagActivityError={tagActivityError}
          clearBasicTagActivity={clearBasicTagActivity}
        />
      </Modal>
      <PageHeaderLayout
        title={
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
            <StarOutlined
              style={{
                marginRight: '8px',
                color: 'lightgrey',
                fontSize: 20,
              }}
            />
            <span>Advertiser</span>
          </div>
        }
        rightContent={
          <Row align="middle" justify="end">
            <Col flex="auto">
              <Space>
                <Button
                  onClick={() => {
                    setOpenLaunch(!openLaunch);
                  }}
                  type="primary"
                  ghost
                  icon={<FormOutlined />}
                  style={{
                    fontSize: isMobile ? '12px' : '14px',
                    padding: isMobile ? '4px 8px' : undefined,
                  }}
                >
                  {!isMobile && <ButtonText text={'Add Funnel'} />}
                  {isMobile && <ButtonText text={'Add'} />}
                </Button>
              </Space>
            </Col>
          </Row>
        }
      />
      <PageContentLayout>
        <Card
          title={
            <Row align="middle">
              <Col>
                <h3 style={{ fontSize: isMobile ? '16px' : '20px', margin: 0 }}>
                  Advertising Funnels
                </h3>
              </Col>
            </Row>
          }
          extra={
            !useCardLayout && (
              <Row align="middle">
                <FilterBar orgLevel={orgLevel} setData={handleSetData} data={data} />
              </Row>
            )
          }
          bodyStyle={{ padding: useCardLayout ? (isMobile ? 12 : 16) : 24 }}
        >
          {useCardLayout && (
            <Row style={{ marginBottom: 16 }}>
              <Col span={24}>
                <FilterBar orgLevel={orgLevel} setData={handleSetData} data={data} />
              </Col>
            </Row>
          )}

          {useCardLayout ? (
            <div>
              {tableData && tableData.length > 0 ? (
                tableData
                  .slice((current - 1) * pageSize, current * pageSize)
                  .map((record) => <div key={record.id}>{renderMobileCard(record)}</div>)
              ) : (
                <div style={{ textAlign: 'center', padding: isMobile ? 24 : 48 }}>
                  <Typography.Text type="secondary">No data available</Typography.Text>
                </div>
              )}
              {tableData && tableData.length > 0 && (
                <Row justify="center" style={{ marginTop: isMobile ? 16 : 24, marginBottom: isMobile ? 16 : 24 }}>
                  <Col>
                    <Pagination
                      current={current}
                      total={tableData.length}
                      pageSize={pageSize}
                      showSizeChanger
                      showQuickJumper
                      size="default"
                      onChange={(page, size) => {
                        setCurrent(page);
                        sessionStorage.setItem('advertiserMainCurrentPage', page.toString());
                        if (size !== pageSize) {
                          setPageSize(size);
                          sessionStorage.setItem('advertiserMainPageSize', size.toString());
                        }
                      }}
                      onShowSizeChange={(current, size) => {
                        setPageSize(size);
                        setCurrent(1);
                        sessionStorage.setItem('advertiserMainCurrentPage', '1');
                        sessionStorage.setItem('advertiserMainPageSize', size.toString());
                      }}
                      itemRender={(page, type, originalElement) => {
                        if (type === 'page') {
                          return (
                            <a style={{
                              display: 'inline-block',
                              borderRadius: '50%',
                              minWidth: '32px',
                              height: '32px',
                              lineHeight: '30px',
                              textAlign: 'center'
                            }}>
                              {page}
                            </a>
                          );
                        }
                        return originalElement;
                      }}
                    />
                  </Col>
                </Row>
              )}
            </div>
          ) : (
            <Table
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              hideOnSinglePage: false,
              pageSize: pageSize,
              current: current,
              size: 'default',
              onChange: (page, size) => {
                setCurrent(page);
                sessionStorage.setItem('advertiserMainCurrentPage', page.toString());
                if (size !== pageSize) {
                  setPageSize(size);
                  sessionStorage.setItem('advertiserMainPageSize', size.toString());
                }
              },
              onShowSizeChange: (current, size) => {
                setPageSize(size);
                setCurrent(1);
                sessionStorage.setItem('advertiserMainCurrentPage', '1');
                sessionStorage.setItem('advertiserMainPageSize', size.toString());
              },
              itemRender: (page, type, originalElement) => {
                if (type === 'page') {
                  return (
                    <a style={{
                      display: 'inline-block',
                      borderRadius: '50%',
                      minWidth: '32px',
                      height: '32px',
                      lineHeight: '30px',
                      textAlign: 'center'
                    }}>
                      {page}
                    </a>
                  );
                }
                return originalElement;
              },
            }}
            columns={columns}
            dataSource={tableData}
            onChange={(pagination, filters, sorter, extra) => {
              if (sorter && sorter.columnKey) {
                const newSortedInfo = {
                  columnKey: sorter.columnKey,
                  order: sorter.order,
                };
                setSortedInfo(newSortedInfo);
                sessionStorage.setItem('advertiserMainSorting', JSON.stringify(newSortedInfo));
              } else {
                setSortedInfo({});
                sessionStorage.removeItem('advertiserMainSorting');
              }
            }}
            onRow={(record, rowIndex) => {
              return {
                onClick: (event) => {
                  history.push(`/advertiser/details/${record.id}`);
                },
              };
            }}
          />
          )}
        </Card>
      </PageContentLayout>
    </React.Fragment>
  );
};

export default withRouter(AdvertiserMainView);
